import { useEffect } from "react";
import { aiExamples } from "./aiExampleTemplates";
import { CopilotIcon } from "./CopilotIcon";
import { useAiIntroduction, type AiIntroductionOptions } from "../hooks/useAiIntroduction";
import "./ai-showcase.css";

export interface AiIntroductionProps extends AiIntroductionOptions {
  onSetup: () => void;
  onExample: () => void;
  onVisibilityChange?: (visible: boolean) => void;
}

export function AiIntroduction(props: AiIntroductionProps) {
  const { visible, busy, error, dismiss, retry } = useAiIntroduction(props);
  const { onVisibilityChange } = props;
  useEffect(() => {
    onVisibilityChange?.(visible);
  }, [onVisibilityChange, visible]);

  if (!visible)
    return error ? (
      <div role="alert" className="ai-preference-error">
        {error}{" "}
        <button className="ai-text-link" onClick={() => void retry()}>
          Try again
        </button>
      </div>
    ) : null;
  return (
    <section className="ai-home-invitation" aria-label="AI introduction">
      <div className="ai-invitation-header">
        <div className="ai-invitation-copy">
          <h2>Create a diagram with AI</h2>
          <p>From a quick sketch to your project's architecture.</p>
        </div>
        <button
          type="button"
          className="ai-hide-intro"
          disabled={busy}
          onClick={async () => {
            if (await dismiss())
              document.querySelector<HTMLButtonElement>(".ai-toolbar-trigger")?.focus();
          }}
        >
          {busy ? "Hiding…" : "Hide introduction"}
        </button>
      </div>
      <div className="ai-home-examples">
        {aiExamples.map((example) => (
          <article className="ai-home-example" key={example.label}>
            <img src={example.image} alt={example.alt} />
            <div>
              <h3>{example.label}</h3>
              <p>{example.summary}</p>
            </div>
          </article>
        ))}
      </div>
      <div className="ai-invitation-actions">
        {props.access === "local-only" ? (
          <button type="button" className="ai-text-link" onClick={props.onSetup}>
            Get full access
          </button>
        ) : (
          <button type="button" className="ai-text-link" onClick={props.onSetup}>
            <span className="ai-introduction-setup-label">
              <CopilotIcon />
              Set up GitHub Copilot
            </span>
          </button>
        )}
        {props.access !== "local-only" && (
          <button className="ai-text-link" onClick={props.onExample}>
            Already set up? Try example
          </button>
        )}
      </div>
      {error && <p role="alert">{error}</p>}
    </section>
  );
}
